
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { SankeySideChart, SankeyGroupedViewDefn } from 'configuration-schemas/viewdefns/viewdefn';

export interface SankeyNode {
  id: string;
  name: string;
  memberId?: string;
  level?: number;
  dimKey?: string;
}

export interface SankeyLink {
  from: string;
  to: string;
  weight: number;
}

export interface SankeyHighchartsPayload {
  nodes: Array<{ id: string; name?: string; color?: string; offset?: number; custom?: any }>;
  data: Array<[string, string, number]>;
  links: SankeyLink[];
}


const EXCLUDED_NUMERIC_KEYS = new Set([
  'children',
  'id',
  'name',
  'description',
  '@path',
  'leaf',
  'expanded',
  '_aggindx',
  '_parentindx',
]);

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').trim();
    if (!cleaned) return undefined;
    const n = Number(cleaned);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

const isNumericValue = (value: unknown) => toNumber(value) != null;

const findFirstNumericKey = (item: BasicPivotItem): string | undefined => {
  for (const [key, value] of Object.entries(item)) {
    if (EXCLUDED_NUMERIC_KEYS.has(key)) continue;
    if (isNumericValue(value)) return key;
  }
  return undefined;
};

const resolveMetricKey = (data: BasicPivotItem[], metricIndex?: string): string | undefined => {
  if (metricIndex) {
    const hasMetric = data.some((item) => isNumericValue(item[metricIndex]));
    if (hasMetric) return metricIndex;
  }
  // If no valid metricIndex provided, attempt to get it from the data (looking for first numeric field that isn't in the excluded list)
  for (const item of data) {
    const key = findFirstNumericKey(item);
    if (key) return key;
  }
  return undefined;
};

function normalizeDimKey(dim: string): string {
  return dim.replace(/^level:/, '').replace(/^attribute:/, '').replace(/^member:/, '');
}


function readMemberId(row: BasicPivotItem, dimKey: string): string | undefined {
  const v = row[`member:${dimKey}:id`];
  return typeof v === 'string' && v ? v : undefined;
}

function readMemberName(row: BasicPivotItem, dimKey: string): string | undefined {
  const name = row[`member:${dimKey}:name`];
  if (typeof name === 'string' && name) return name;

  const desc = row[`member:${dimKey}:description`];
  if (typeof desc === 'string' && desc) return desc;

  // fallback to raw field if present
  const raw = row[dimKey];
  if (typeof raw === 'string' && raw) return raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw);
  return undefined;
}

// Highcharts node ids must be stable and unique across levels.
// Use "<level>:<memberId>" so same memberId at different levels doesn't collide.
function makeNodeId(level: number, memberIdOrName: string) {
  return `${level}:${memberIdOrName}`;
}

/**
 * Build a Sankey graph from flat pivot rows.
 * - grouping: ordered dimension keys
 * - metricIndex: value field used as weight
 *
 * Output:
 * - nodes: one per unique member per level
 * - links: aggregated weights between consecutive levels
 */
export function buildSankeyGraph(
  rows: BasicPivotItem[],
  metricIndex?: string,
  grouping?: string[],
  rootLabel: string = 'All products',
) {
  const nodesById: Record<string, SankeyNode> = {};
  const linkWeightByKey: Record<string, number> = {};
  const links: SankeyLink[] = [];

  const resolvedMetric = resolveMetricKey(rows, metricIndex);

  // Root node
  const rootId = makeNodeId(0, 'ROOT');
  nodesById[rootId] = { id: rootId, name: rootLabel, level: 0, dimKey: 'root' };
  const safeGrouping = (grouping ?? []).filter(Boolean).map(normalizeDimKey);

  // If no grouping, return just the root (prevents empty / errors)
  if (!safeGrouping.length) {
    return { nodes: Object.values(nodesById), links: [] as SankeyLink[] };
  }

  for (const row of rows) {
    const weightRaw =
      (resolvedMetric ? row[resolvedMetric] : undefined) ??
      row['eoh_units'] ??
      row['value'] ??
      row['units'];

    const w = toNumber(weightRaw) ?? 0;
    if (!w) continue;

    let prevNodeId = rootId;

    safeGrouping.forEach((dimKey, idx) => {
      const level = idx + 1;

      const memberId = readMemberId(row, dimKey);
      const name = readMemberName(row, dimKey) ?? memberId ?? 'Unknown';

      // If no memberId, fall back to name as a key
      const key = memberId ?? name;
      const nodeId = makeNodeId(level, key);

      if (!nodesById[nodeId]) {
        nodesById[nodeId] = {
          id: nodeId,
          name,
          memberId,
          level,
          dimKey,
        };
      }

      const linkKey = `${prevNodeId}__${nodeId}`;
      linkWeightByKey[linkKey] = (linkWeightByKey[linkKey] ?? 0) + w;

      prevNodeId = nodeId;
    });
  }

  for (const [k, weight] of Object.entries(linkWeightByKey)) {
    if (!weight) continue;
    const [from, to] = k.split('__');
    links.push({ from, to, weight });
  }

  return { nodes: Object.values(nodesById), links };
}

// build Highcharts-ready nodes/data while preserving raw graph
export function buildHighchartsSankey(
  rows: BasicPivotItem[],
  metricIndex?: string,
  grouping?: string[],
  rootLabel: string = 'All products',
): SankeyHighchartsPayload {
  const meta = buildSankeyGraph(rows, metricIndex, grouping, rootLabel);

  const nodes = meta.nodes.map((n) => ({
    id: n.id,
    name: n.name,
    custom: { memberId: n.memberId, dimKey: n.dimKey, level: n.level },
  }));

  const data = meta.links.map((l) => [l.from, l.to, l.weight] as [string, string, number]);

  return { nodes, data, links: meta.links };
}

// Build a single Highcharts pie series from detail items (assumed already filtered by the pivot request).
export const buildPieSeries = (items: BasicPivotItem[], chart: SankeySideChart) => {
  const points = items.map((item) => {
    const valRaw = item[chart.dataIndex];
    const y = toNumber(valRaw) ?? 0;
    const name =
      item.name ||
      item.description ||
      item.id;
    return { name, y };
  });
  return {
    name: chart.text || chart.dataIndex,
    data: points,
  };
};




// Default grouping path pulled from the view defn defaults.
export function selectSankeyGroupPath(defn?: SankeyGroupedViewDefn): string[] {
  return defn?.defaults?.grouping ?? []
}
